import React, { useCallback, useEffect, useMemo, useState } from 'react';

import type { DocParts } from '@aline/common';
import {
  AlineTransforms,
  createDocumentRoute,
  defaultWorkflowFileSort,
  getDocumentRoute,
  getEditorWorkflowRoute,
} from '@aline/common';
import { link } from '@aline/routes';
import { useReactiveVar } from '@apollo/client/react';
import { Typography } from '@mui/material';
import clsx from 'clsx';
import { formatDistance, isAfter, parseISO } from 'date-fns';
import { uniqBy } from 'ramda';
import { Link } from 'react-router-dom';
import { usePrevious } from 'react-use';
import { AlineAiAvatar, AlineAvatar, AlineAvatarGroup } from 'src/components/AlineAvatar';
import { AlineCheckbox, AlineTooltip, Spacer } from 'src/components/generic';
import { AlertBellIcon } from 'src/components/icons/AlertBellIcon';
import { DocumentIcon } from 'src/components/icons/DocumentIcon';
import { MyTaskIcon } from 'src/components/icons/MyTaskIcon';
import { SigningIcon } from 'src/components/icons/SigningIcon';
import { SmallSignatureIcon } from 'src/components/icons/SmallSignatureIcon';
import { StageIcon } from 'src/components/icons/StageIcon';
import { WorkflowIcon } from 'src/components/icons/WorkflowIcon';
import { RemindRecipientsDialog } from 'src/components/workflow';
import { nestSubtasks } from 'src/components/workflow/workflow-detail/side-panel/task-review/task-review-helpers';
import { TaskReviewStatusPill } from 'src/components/workflow/workflow-detail/side-panel/task-review/TaskStatusPill';
import {
  ReopenTaskDialog,
  useReopenTask,
} from 'src/components/workflow/workflow-detail/side-panel/task-review/use-reopen-task';
import { Apollo } from 'src/gql/generated';
import { useCurrentUserId } from 'src/gql/hooks';
import {
  getAssigneeDisplayDetails,
  getIsTaskPreSigning,
  getPermissionAssigneeDisplayDetails,
  getPermissionUser,
  openLinkFromAnchorClick,
} from 'src/helpers';
import { getSortCommentsComparator, useChangeTaskStatus } from 'src/hooks';
import type { UserDefinedRoleSubset, UserSubset } from 'src/types';
import { makeStyles } from 'tss-react/mui';

import type { WorkflowInboxWorkflow } from './WorkflowsInbox';
import { WorkflowInboxFilter, workflowInboxFilterVar } from './WorkflowsInbox';

const actionButtonId = 'action-button';
const useStyles = makeStyles()(theme => ({
  checkbox: {
    padding: 0,
  },
  signButton: {
    height: 24,
    visibility: 'hidden',
    width: 24,
  },
  subtask: {
    paddingLeft: theme.spacing(5),
  },
  task: {
    /** The row's link is stretched across it, so the focus ring belongs on the row. */
    '&:has(a:focus-visible)': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
    '&:hover': {
      backgroundColor: theme.appColors.background,
    },
    [`&:hover #${actionButtonId}`]: {
      visibility: 'visible',
    },
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    // Containing block for the task link's stretched ::after.
    position: 'relative',
    textDecoration: 'none',
  },
  /** Above the task link's stretched ::after so these stay independently interactive. */
  taskControl: {
    alignItems: 'center',
    display: 'flex',
    position: 'relative',
    zIndex: 2,
  },
  taskHeader: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(0, 2),
  },
  /**
   * Carries the task title as real link text and stretches over the row with `::after`, so the
   * whole row opens the task without nesting the checkbox inside an anchor.
   */
  taskLink: {
    '&::after': {
      content: '""',
      inset: 0,
      position: 'absolute',
      zIndex: 1,
    },
    '&:focus-visible': { outline: 'none' },
    color: 'inherit',
    minWidth: 0,
    textDecoration: 'none',
  },
  taskList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
}));

type WorkflowTaskRow = Pick<
  Apollo.Task,
  'id' | 'title' | 'completedAt' | 'isDelegatedToAi' | 'status'
> & {
  assignments: {
    permission: Pick<Apollo.WorkflowPermissionSimpleFragment, 'account' | 'id' | 'role'>;
  }[];
  phase: { id: string } | null;
};

export type PhaseWithOrder = Pick<Apollo.Phase, 'id' | 'order'> & {
  __typename: Apollo.NonSigningPhase['__typename'] | Apollo.SigningPhase['__typename'];
};

type InboxWorkflow = Pick<Apollo.Workflow, 'state' | '__typename'> & {
  phases: Array<PhaseWithOrder>;
};

export function useGetIsInboxTask(): {
  getIsInboxTask: (
    task: WorkflowTaskRow,
    workflow: InboxWorkflow,
    filter: WorkflowInboxFilter
  ) => boolean;
} {
  const currentUserId = useCurrentUserId();
  const safeGetIsInboxTask = useCallback(
    (task: WorkflowTaskRow, workflow: InboxWorkflow, filter: WorkflowInboxFilter): boolean => {
      const isIncompleteTask = !task.completedAt;
      const isAssignedToUser = task.assignments.some(
        ({ permission }) => getPermissionUser(permission)?.id === currentUserId
      );
      const isRequiredBeforeSigning = getIsTaskPreSigning(task, workflow.phases);

      switch (workflow.state) {
        case Apollo.WorkflowState.Negotiating:
        case Apollo.WorkflowState.ReadyForSignature:
          return filter === WorkflowInboxFilter.All
            ? isIncompleteTask && isRequiredBeforeSigning
            : isIncompleteTask && isRequiredBeforeSigning && isAssignedToUser;
        case Apollo.WorkflowState.Signing:
          // We don't want any tasks to show up during signing as they are not blocking moving to the next workflow stage.
          return false;
        case Apollo.WorkflowState.Signed:
        case Apollo.WorkflowState.Complete:
          return filter === WorkflowInboxFilter.All
            ? isIncompleteTask
            : isIncompleteTask && isAssignedToUser;
        case Apollo.WorkflowState.Template:
          return false;
      }
    },
    [currentUserId]
  );

  return { getIsInboxTask: safeGetIsInboxTask };
}

export function WorkflowTasks({
  onClose,
  openInNewTab,
  workflow,
}: {
  openInNewTab: boolean;
  workflow: WorkflowInboxWorkflow;
  onClose?: () => void;
}) {
  const { classes } = useStyles();
  const { getIsInboxTask } = useGetIsInboxTask();
  const filter = useReactiveVar(workflowInboxFilterVar);
  const previousFilter = usePrevious(filter);
  const [incompleteTaskIds, setIncompleteTaskIds] = useState<string[]>([]);
  const incompleteTasks = workflow.tasks.filter(task => incompleteTaskIds.includes(task.id));

  useEffect(() => {
    // Ensure effect doesn't run in an infinite loop.
    if (!workflow.tasks.length) return;

    // Only filter tasks on initial load or when filter changes
    if (!incompleteTaskIds.length || previousFilter !== filter)
      setIncompleteTaskIds(
        workflow.tasks.filter(task => getIsInboxTask(task, workflow, filter)).map(task => task.id)
      );
  }, [filter, getIsInboxTask, incompleteTaskIds.length, previousFilter, workflow]);

  if (!incompleteTasks.length) return null;

  return (
    <div className={classes.taskList}>
      <div className={classes.taskHeader}>
        <MyTaskIcon className={classes.checkbox} />
        <Typography variant="subtitle1">Incomplete Tasks</Typography>
      </div>
      {nestSubtasks(incompleteTasks).map(({ subtasks, task }) => (
        <React.Fragment key={task.id}>
          <WorkflowTaskRow
            openInNewTab={openInNewTab}
            task={task}
            workflowId={workflow.id}
            onClose={onClose}
          />
          {subtasks.map(subtask => (
            <WorkflowTaskRow
              key={subtask.id}
              isSubtask
              openInNewTab={openInNewTab}
              task={subtask}
              workflowId={workflow.id}
              onClose={onClose}
            />
          ))}
        </React.Fragment>
      ))}
    </div>
  );
}

export function WorkflowTaskRow({
  isSubtask = false,
  onClose,
  openInNewTab,
  task,
  workflowId,
}: {
  openInNewTab: boolean;
  task: WorkflowTaskRow;
  workflowId: UUID;
  isSubtask?: boolean;
  onClose?: () => void;
}) {
  const { classes } = useStyles();
  const reopen = useReopenTask(task.id);
  const { cannotChangeStatusReason, markComplete } = useChangeTaskStatus(task.id);
  const taskHref = link.WorkflowDetailTabTask({ taskId: task.id, workflowId });

  return (
    <div className={clsx(classes.task, isSubtask && classes.subtask)}>
      <div className={classes.taskControl}>
        <AlineTooltip
          arrow
          placement="top"
          title={
            cannotChangeStatusReason
              ? cannotChangeStatusReason
              : task.completedAt
                ? 'Mark task incomplete'
                : 'Mark task complete'
          }
        >
          <AlineCheckbox
            checked={Boolean(task.completedAt)}
            className={classes.checkbox}
            onChange={async () => {
              if (!task.completedAt) await markComplete();
              else reopen.requestReopen();
            }}
          />
        </AlineTooltip>
      </div>
      <Link
        className={classes.taskLink}
        to={taskHref}
        onClick={e => {
          if (openInNewTab) openLinkFromAnchorClick(e, taskHref);
          onClose?.();
        }}
      >
        <Typography variant="body1">{task.title || 'Untitled'}</Typography>
      </Link>
      <TaskReviewStatusPill status={task.status} />
      <Spacer />
      <div className={classes.taskControl}>
        <AlineAvatarGroup>
          {task.assignments.map(({ permission }) => (
            <AlineTooltip
              key={permission.id}
              arrow
              placement="top"
              title={getPermissionAssigneeDisplayDetails(permission).primary}
            >
              <AlineAvatar avatarInfo={getPermissionUser(permission)} />
            </AlineTooltip>
          ))}
          {task.isDelegatedToAi && (
            <AlineTooltip arrow placement="top" title="Aline AI">
              <AlineAiAvatar />
            </AlineTooltip>
          )}
        </AlineAvatarGroup>
      </div>
      <ReopenTaskDialog
        nextRound={reopen.nextRound}
        open={reopen.isPromptOpen}
        onClose={reopen.dismissPrompt}
        onConfirm={reopen.confirmReopen}
      />
    </div>
  );
}

interface CommentWithAuthor extends Pick<
  Apollo.Comment,
  'id' | 'deletedAt' | 'nodeRefId' | 'resolved' | '__typename'
> {
  author: UserSubset | null;
}
export type DocumentWithCommentsVersion =
  | Pick<Apollo.StaticFileVersion, 'id' | '__typename'>
  | Pick<Apollo.DocumentVersion, 'documentJSON' | 'id' | '__typename'>;

export function getIsCommentInDocAndUnresolved(
  comment: Pick<Apollo.Comment, 'id' | 'deletedAt' | 'nodeRefId' | 'resolved' | '__typename'>
): boolean {
  return Boolean(!comment.deletedAt && !comment.resolved && comment.nodeRefId);
}

export function useGetIsInboxComment(): {
  getIsInboxComment: (
    comment: CommentWithAuthor,
    workflow: Pick<Apollo.Workflow, 'state' | '__typename'>,
    filter: WorkflowInboxFilter
  ) => boolean;
} {
  const currentUserId = useCurrentUserId();
  const safeGetIsInboxComment = useCallback(
    (
      comment: CommentWithAuthor,
      workflow: Pick<Apollo.Workflow, 'state' | '__typename'>,
      filter: WorkflowInboxFilter
    ): boolean => {
      switch (workflow.state) {
        case Apollo.WorkflowState.Negotiating:
        case Apollo.WorkflowState.ReadyForSignature: {
          const isCommentInDocAndUnresolved = getIsCommentInDocAndUnresolved(comment);
          const isUserAuthor = comment.author?.id === currentUserId;

          return filter === WorkflowInboxFilter.All
            ? isCommentInDocAndUnresolved
            : isCommentInDocAndUnresolved && isUserAuthor;
        }
        default:
          // We don't want any comments to show up after sending for signature since comments are not blocking moving to the next workflow stage.
          return false;
      }
    },
    [currentUserId]
  );

  return { getIsInboxComment: safeGetIsInboxComment };
}

export function WorkflowFilesIssues({
  onClose,
  workflow,
}: {
  workflow: WorkflowInboxWorkflow;
  onClose?: () => void;
}) {
  const { classes } = useStyles();
  const filter = useReactiveVar(workflowInboxFilterVar);
  const { getIsInboxComment } = useGetIsInboxComment();
  // ToDo: Also handle files with unresolved tracked changes.
  const filesWithUnresolvedComments = useMemo(
    () =>
      workflow.files.filter(file =>
        file.document.comments.some(comment => getIsInboxComment(comment, workflow, filter))
      ),
    [filter, getIsInboxComment, workflow]
  );

  if (!filesWithUnresolvedComments.length) return null;

  return (
    <div className={classes.taskList}>
      {filesWithUnresolvedComments.map(file => {
        const inboxComments = file.document.comments.filter(comment =>
          getIsInboxComment(comment, workflow, filter)
        );
        const editor =
          file.document.latestVersion.__typename === 'DocumentVersion'
            ? AlineTransforms.createServerEditorFromJson(
                file.document.latestVersion.documentJSON as DocParts,
                {
                  state: {
                    editingUserId: null,
                    isInSigningCeremony: false,
                    isReadOnly: false,
                    isSigned: false,
                  },
                }
              )
            : null;
        const uniqCommentAuthors = uniqBy(
          (author: Pick<Apollo.User, 'id' | '__typename'> | null) => author?.id,
          inboxComments.flatMap(comment => {
            return comment.author ? [comment.author] : [];
          })
        );

        const commentsSortedByDocPosition = editor
          ? [...inboxComments].sort(getSortCommentsComparator(editor))
          : inboxComments;
        const firstUnresolvedComment = commentsSortedByDocPosition.at(0);

        return (
          <React.Fragment key={file.id}>
            <div className={classes.taskHeader}>
              <DocumentIcon className={classes.checkbox} />
              <Typography variant="subtitle1">{file.document.name || 'Untitled'}</Typography>
            </div>
            <Link
              className={classes.task}
              to={
                // Should open editor to the first unresolved comment if it exists, otherwise open the document.
                firstUnresolvedComment?.nodeRefId
                  ? createDocumentRoute(file.document.id, {
                      commentId: firstUnresolvedComment.nodeRefId,
                    })
                  : getDocumentRoute(file.document.id)
              }
              onClick={e => {
                e.stopPropagation();
                onClose?.();
              }}
            >
              <StageIcon />
              <Typography variant="body1">
                {file.document.comments.length > 1
                  ? `${inboxComments.length} unresolved comments`
                  : '1 unresolved comment'}
              </Typography>
              <Spacer />
              <AlineAvatarGroup max={2}>
                {uniqCommentAuthors.map(author => (
                  <AlineTooltip key={author.id} arrow placement="top" title={author.displayName}>
                    <AlineAvatar avatarInfo={author} />
                  </AlineTooltip>
                ))}
              </AlineAvatarGroup>
            </Link>
          </React.Fragment>
        );
      })}
    </div>
  );
}

interface WorkflowUnusedAction extends Pick<Apollo.WorkflowRecipient, 'createdAt' | 'id' | 'type'> {
  assignee: UserSubset | null;
  assigneeRole: UserDefinedRoleSubset | null;
}

export function useGetIsInboxUnusedAction(): {
  getIsInboxUnusedAction: (action: WorkflowUnusedAction, filter: WorkflowInboxFilter) => boolean;
} {
  const currentUserId = useCurrentUserId();
  const safeGetIsInboxUnusedAction = useCallback(
    (action: WorkflowUnusedAction, filter: WorkflowInboxFilter) => {
      switch (filter) {
        case WorkflowInboxFilter.AssignedToMe:
          return action.assignee?.id === currentUserId;
        default:
          return true;
      }
    },
    [currentUserId]
  );

  return { getIsInboxUnusedAction: safeGetIsInboxUnusedAction };
}

export function WorkflowIssues({
  onClose,
  openInNewTab,
  workflow,
}: {
  openInNewTab: boolean;
  workflow: WorkflowInboxWorkflow;
  onClose?: () => void;
}) {
  const { classes } = useStyles();
  const filter = useReactiveVar(workflowInboxFilterVar);
  const { getIsInboxUnusedAction } = useGetIsInboxUnusedAction();
  const inboxUnusedActions = workflow.unusedActions.filter(action =>
    getIsInboxUnusedAction(action, filter)
  );

  if (!inboxUnusedActions.length && !workflow.missingActions) return null;

  const firstDocumentId = workflow.files.at(0)?.document.id;
  const stepsHref = link.WorkflowDetailTabSteps({ workflowId: workflow.id });

  return (
    <div className={classes.taskList}>
      <div className={classes.taskHeader}>
        <WorkflowIcon className={classes.checkbox} />
        <Typography variant="subtitle1">Workflow Issues</Typography>
      </div>
      {workflow.unusedActions.map(action => {
        const { avatarColor, primary: signerDisplayName } = getAssigneeDisplayDetails(
          action.assignee,
          action.assigneeRole
        );

        return (
          <Link
            key={action.id}
            className={classes.task}
            to={stepsHref}
            onClick={e => {
              e.stopPropagation();
              if (openInNewTab) openLinkFromAnchorClick(e, stepsHref);
              onClose?.();
            }}
          >
            <StageIcon />
            <Typography variant="body1">
              {`Signer "${signerDisplayName}" has no signatures in any document`}
            </Typography>
            <Spacer />
            <AlineTooltip arrow placement="top" title={signerDisplayName}>
              <AlineAvatar
                avatarInfo={action.assignee ?? action.assigneeRole}
                styles={{ backgroundColor: avatarColor }}
              />
            </AlineTooltip>
          </Link>
        );
      })}
      {workflow.missingActions && (
        <Link
          className={classes.task}
          to={
            firstDocumentId
              ? link.DocumentDetail({ documentId: firstDocumentId })
              : link.WorkflowDetailTabSteps({ workflowId: workflow.id })
          }
          onClick={e => {
            e.stopPropagation();
            onClose?.();
          }}
        >
          <StageIcon />
          <Typography variant="body1">
            There are unassigned fields in some workflow documents.
          </Typography>
          <Spacer />
          <AlineTooltip arrow placement="top" title="Unassigned">
            <AlineAvatar />
          </AlineTooltip>
        </Link>
      )}
    </div>
  );
}

interface InboxSigningRecipient extends Pick<
  Apollo.WorkflowRecipient,
  'completedAt' | 'id' | 'type'
> {
  assignee: UserSubset | null;
}

export function useGetIsInboxSignature(): {
  getIsInboxSignature: (
    workflow: Pick<Apollo.Workflow, 'state' | '__typename'>,
    recipient: InboxSigningRecipient,
    filter: WorkflowInboxFilter
  ) => boolean;
} {
  const currentUserId = useCurrentUserId();
  const safeGetIsInboxSignature = useCallback(
    (
      workflow: Pick<Apollo.Workflow, 'state' | '__typename'>,
      recipient: InboxSigningRecipient,
      filter: WorkflowInboxFilter
    ): boolean => {
      if (workflow.state !== Apollo.WorkflowState.Signing) return false;

      const isIncompleteSignature =
        recipient.type === Apollo.WorkflowRecipientType.Signer && !recipient.completedAt;

      switch (filter) {
        case WorkflowInboxFilter.All:
          return isIncompleteSignature;
        case WorkflowInboxFilter.AssignedToMe:
          return Boolean(isIncompleteSignature && recipient.assignee?.id === currentUserId);
      }
    },
    [currentUserId]
  );

  return { getIsInboxSignature: safeGetIsInboxSignature };
}

export function ActiveSigningGroup({
  onClose,
  workflow,
}: {
  workflow: WorkflowInboxWorkflow;
  onClose?: () => void;
}): JSX.Element | null {
  const { classes } = useStyles();
  const currentUserId = useCurrentUserId();
  const filter = useReactiveVar(workflowInboxFilterVar);
  const { getIsInboxSignature } = useGetIsInboxSignature();
  const incompleteSigners = workflow.activeStep?.actions.filter(recipient =>
    getIsInboxSignature(workflow, recipient, filter)
  );
  const [remindDialogOpen, setRemindDialogOpen] = useState<boolean>(false);

  if (!incompleteSigners?.length) return null;

  return (
    <div className={classes.taskList}>
      <div className={classes.taskHeader}>
        <SigningIcon />
        <Typography variant="subtitle1">Awaiting signatures</Typography>
      </div>
      {incompleteSigners.map(signer => {
        const lastRemindedAt = getActionLastRemindedAt(signer);
        const isUserSigner = signer.assignee?.id === currentUserId;
        const signerName = signer.assignee?.displayName || 'Unassigned';
        const firstWorkflowFile = [...workflow.files].sort(defaultWorkflowFileSort).at(0);
        const editorRoute = firstWorkflowFile
          ? getEditorWorkflowRoute(firstWorkflowFile.document.id, workflow.id, {
              walkThroughParams: { autoStartWalkthrough: true },
            })
          : null;

        return (
          <Link
            key={signer.id}
            className={classes.task}
            to={editorRoute ?? link.WorkflowDetailTabSteps({ workflowId: workflow.id })}
            onClick={e => {
              e.stopPropagation();

              // If they are not the signer, simply allow them to remind the signer to sign...
              if (!isUserSigner) {
                e.preventDefault();
                setRemindDialogOpen(true);
                return;
              }

              // Nothing to sign yet — stay put rather than sending them somewhere unrelated.
              if (!editorRoute) e.preventDefault();
              onClose?.();
            }}
          >
            <StageIcon />
            <div>
              <Typography noWrap>{signerName}</Typography>
              {lastRemindedAt && (
                <Typography noWrap color="textSecondary" variant="caption">
                  {`Last reminded ${formatDistance(parseISO(lastRemindedAt), new Date())} ago`}
                </Typography>
              )}
            </div>
            <Spacer />
            <AlineTooltip
              arrow
              id={actionButtonId}
              placement="top"
              title={isUserSigner ? 'Complete signature' : `Remind "${signerName}" to sign`}
            >
              {isUserSigner ? <SmallSignatureIcon /> : <AlertBellIcon />}
            </AlineTooltip>
            <AlineTooltip arrow placement="top" title={signerName}>
              <AlineAvatar avatarInfo={signer.assignee} />
            </AlineTooltip>
          </Link>
        );
      })}
      <RemindRecipientsDialog
        isOpen={remindDialogOpen}
        workflowId={workflow.id}
        onClose={() => setRemindDialogOpen(false)}
      />
    </div>
  );
}

type RemindableAction = Pick<
  Apollo.WorkflowAction,
  'createdAt' | 'firstSentAt' | 'id' | 'lastSentAt' | '__typename'
>;

export function getActionLastRemindedAt(action: RemindableAction): string | null {
  if (!action.firstSentAt || !action.lastSentAt) return null;

  const wasReminded = isAfter(parseISO(action.lastSentAt), parseISO(action.firstSentAt));
  return wasReminded ? action.lastSentAt : null;
}
